/** Volume buckets stored in D1 */
export const VOLUME_BUCKETS = ["trace", "small", "medium", "large", "soak"] as const;
export type VolumeBucket = (typeof VOLUME_BUCKETS)[number];

export type LightLevel = "low" | "medium" | "bright" | "direct_ok";

/** How the plant is grown at home (user-edited). */
export const GROWING_MEDIA = ["hydroponic", "soil"] as const;
export type GrowingMedium = (typeof GROWING_MEDIA)[number];

/** Text-only enrichment (Call B) — Jeopardy-style + care; keep claims hedged in UI. */
export interface AiPlantEncyclopedia {
  latin_etymology: string;
  origin_and_habitat: string;
  biome_notes: string;
  jeopardy_style_factoids: string[];
  indoor_space_benefits: string;
  overall_confidence: number;
  sources_summary: string;
}

export interface AiPlantCare {
  water: string;
  light: string;
  humidity: string;
  soil: string;
  fertilizer: string;
  common_issues: string;
}

export interface AiPlantProfile {
  common_name_guess: string;
  scientific_name_guess?: string;
  light: LightLevel;
  watering_style_notes: string;
  disclaimer: string;
  /** 0..1 from vision step */
  vision_confidence?: number;
  encyclopedia?: AiPlantEncyclopedia | null;
  care?: AiPlantCare | null;
}

export interface ScheduleHintPayload {
  suggested_interval_days: number;
  suggested_interval_days_max?: number;
  next_suggested_at_ms: number;
  confidence: number;
  rationale_short: string;
}

export interface PlantPhoto {
  id: string;
  url: string;
  created_at: number;
  is_cover: boolean;
}

export type DiagnosisSeverity = "low" | "medium" | "high" | "unknown";

export interface AiPlantDiagnosis {
  summary: string;
  possible_issues: string[];
  severity: DiagnosisSeverity;
  recommended_actions: string[];
  growth_notes?: string;
  disclaimer: string;
  confidence: number;
}

export interface PlantDiagnosisRecord {
  id: string;
  created_at: number;
  photo_ids: string[];
  result: AiPlantDiagnosis;
}

export interface PlantSummary {
  id: string;
  name: string;
  /** Where the plant lives at home (user-edited). */
  location: string | null;
  /** Hydroponic vs potted in soil (user-edited). */
  growing_medium: GrowingMedium | null;
  photoUrl: string;
  photo_count: number;
  created_at: number;
  ai_profile: AiPlantProfile | null;
  ai_profile_updated_at: number | null;
  schedule: {
    next_suggested_at: number | null;
    confidence: number | null;
    rationale_short: string | null;
    updated_at: number | null;
  } | null;
  last_watering: {
    occurred_at: number;
    volume_bucket: VolumeBucket;
    method_text: string;
  } | null;
}

export interface PlantDetail extends PlantSummary {
  photos: PlantPhoto[];
  latest_diagnosis: PlantDiagnosisRecord | null;
  recent_events: {
    id: string;
    occurred_at: number;
    volume_bucket: VolumeBucket;
    method_text: string;
    source: string;
  }[];
}
